import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  LinearProgress,
  Paper,
  Chip,
  IconButton,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle,
  Error,
  Cancel,
  Pending,
  Stop,
  Pause,
  PlayArrow,
} from '@mui/icons-material';
import { useSocket } from '../contexts/SocketContext';
import { apiService } from '../services/api';

interface PersonalTemplateSendProgressProps {
  open: boolean;
  onClose: () => void;
  sessionId: string;
  totalRecords: number;
  templateName: string;
  onComplete?: () => void;
}

interface ProgressUpdate {
  recordId: string;
  status: string;
  recipientNumber: string;
  errorMessage?: string;
  messageId?: string;
}

const WA_DISCONNECT_PAUSE_REASONS = [
  'Personal WhatsApp session is not connected',
  'Personal WhatsApp session disconnected',
];

const isWaDisconnectPauseReason = (reason: string | null | undefined) =>
  !!reason && WA_DISCONNECT_PAUSE_REASONS.includes(reason);

const PersonalTemplateSendProgress: React.FC<PersonalTemplateSendProgressProps> = ({
  open,
  onClose,
  sessionId,
  totalRecords,
  templateName,
  onComplete,
}) => {
  const { socket } = useSocket();
  const [successCount, setSuccessCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [cancelledCount, setCancelledCount] = useState(0);
  const [recentUpdates, setRecentUpdates] = useState<ProgressUpdate[]>([]);
  const [sessionStatus, setSessionStatus] = useState<string>('in_progress');
  const [pausedReason, setPausedReason] = useState<string | null>(null);
  const [nextSendAt, setNextSendAt] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isPausing, setIsPausing] = useState(false);
  const [isResuming, setIsResuming] = useState(false);

  const processedCount = successCount + failedCount + cancelledCount;
  const pendingCount = Math.max(0, totalRecords - processedCount);
  const progress = totalRecords > 0 ? (processedCount / totalRecords) * 100 : 0;
  const isComplete = sessionStatus === 'completed' || sessionStatus === 'cancelled';
  const isPaused = sessionStatus === 'paused';
  const isScheduled = sessionStatus === 'scheduled';

  useEffect(() => {
    setSuccessCount(0);
    setFailedCount(0);
    setCancelledCount(0);
    setRecentUpdates([]);
    setSessionStatus('in_progress');
    setPausedReason(null);
    setNextSendAt(null);
    setIsCancelling(false);
    setIsPausing(false);
    setIsResuming(false);
  }, [sessionId]);

  useEffect(() => {
    if (!open || !sessionId) return;

    const loadSession = async () => {
      try {
        const response = await apiService.getPersonalTemplateSessionDetails(sessionId, {
          page: 1,
          limit: 1,
        });
        const session = response.data.data?.session;
        if (session) {
          setSessionStatus(session.status);
          setSuccessCount(session.successCount || 0);
          setFailedCount(session.failedCount || 0);
          setCancelledCount(session.cancelledCount || 0);
          setPausedReason(session.pausedReason || null);
          setNextSendAt(session.nextSendAt || null);
        }
      } catch (error) {
        console.error('Failed to load session details:', error);
      }
    };

    loadSession();
  }, [open, sessionId]);

  useEffect(() => {
    if (!socket || !open) return;

    const handleProgress = (data: any) => {
      if (data.sessionId !== sessionId) return;

      if (data.status === 'success') {
        setSuccessCount(prev => prev + 1);
      } else if (data.status === 'failed') {
        setFailedCount(prev => prev + 1);
      }

      setRecentUpdates(prev => [
        {
          recordId: data.recordId,
          status: data.status,
          recipientNumber: data.recipientNumber,
          errorMessage: data.errorMessage,
          messageId: data.messageId,
        },
        ...prev.slice(0, 9),
      ]);
    };

    const handleComplete = (data: any) => {
      if (data.sessionId !== sessionId) return;
      setSessionStatus('completed');
      setSuccessCount(data.successCount);
      setFailedCount(data.failedCount);
      setCancelledCount(data.cancelledCount);
      onComplete?.();
    };

    const handleCancelled = (data: any) => {
      if (data.sessionId !== sessionId) return;
      setSessionStatus('cancelled');
      setIsCancelling(false);
    };

    const handlePaused = (data: any) => {
      if (data.sessionId !== sessionId) return;
      setSessionStatus('paused');
      setPausedReason(data.pausedReason || null);
      setNextSendAt(data.pausedUntil || data.nextSendAt || null);
      setIsPausing(false);
    };

    const handleScheduled = (data: any) => {
      if (data.sessionId !== sessionId) return;
      setSessionStatus('scheduled');
      setPausedReason(data.pausedReason || 'Waiting for next send window');
      setNextSendAt(data.nextSendAt || null);
    };

    const handleResumed = (data: any) => {
      if (data.sessionId !== sessionId) return;
      setSessionStatus('in_progress');
      setPausedReason(null);
      setNextSendAt(null);
      setIsResuming(false);
    };

    socket.on('personal_template_send_progress', handleProgress);
    socket.on('personal_template_send_complete', handleComplete);
    socket.on('personal_template_send_cancelled', handleCancelled);
    socket.on('personal_template_send_paused', handlePaused);
    socket.on('personal_template_send_scheduled', handleScheduled);
    socket.on('personal_template_send_resumed', handleResumed);

    return () => {
      socket.off('personal_template_send_progress', handleProgress);
      socket.off('personal_template_send_complete', handleComplete);
      socket.off('personal_template_send_cancelled', handleCancelled);
      socket.off('personal_template_send_paused', handlePaused);
      socket.off('personal_template_send_scheduled', handleScheduled);
      socket.off('personal_template_send_resumed', handleResumed);
    };
  }, [socket, open, sessionId, onComplete]);

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this sending session?')) {
      return;
    }

    setIsCancelling(true);
    try {
      await apiService.cancelPersonalTemplateSend(sessionId);
    } catch (error) {
      console.error('Error cancelling session:', error);
      alert('Failed to cancel session. Please try again.');
      setIsCancelling(false);
    }
  };

  const handlePause = async () => {
    setIsPausing(true);
    try {
      await apiService.pausePersonalTemplateSend(sessionId);
    } catch (error) {
      console.error('Error pausing session:', error);
      alert('Failed to pause session.');
      setIsPausing(false);
    }
  };

  const handleResume = async () => {
    setIsResuming(true);
    try {
      await apiService.resumePersonalTemplateSend(sessionId);
    } catch (error) {
      console.error('Error resuming session:', error);
      alert('Failed to resume session.');
      setIsResuming(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle color="success" fontSize="small" />;
      case 'failed':
        return <Error color="error" fontSize="small" />;
      case 'cancelled':
        return <Cancel color="disabled" fontSize="small" />;
      default:
        return <Pending color="info" fontSize="small" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'failed':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'info';
    }
  };

  const formatNextSend = (value: string | null) => {
    if (!value) return null;
    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Sending: {templateName}</Typography>
          {!isComplete && (
            <Box display="flex" gap={0.5}>
              {!isPaused && (
                <Tooltip title="Pause sending">
                  <IconButton onClick={handlePause} disabled={isPausing} size="small">
                    <Pause />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Cancel sending">
                <IconButton onClick={handleCancel} disabled={isCancelling} color="error" size="small">
                  <Stop />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
              <Chip
                label={`Status: ${sessionStatus}`}
                color={isPaused ? 'warning' : isScheduled ? 'info' : isComplete ? 'default' : 'primary'}
                size="small"
              />
              {pendingCount > 0 && (
                <Chip label={`Remaining: ${pendingCount}`} size="small" variant="outlined" />
              )}
            </Box>

            <Typography variant="subtitle2" gutterBottom>
              Progress: {Math.round(progress)}%
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ mb: 2, height: 8, borderRadius: 1 }}
            />
            <Box display="flex" gap={2} flexWrap="wrap">
              <Chip icon={<Pending />} label={`Pending: ${pendingCount}`} color="info" variant="outlined" />
              <Chip icon={<CheckCircle />} label={`Success: ${successCount}`} color="success" variant={successCount > 0 ? 'filled' : 'outlined'} />
              <Chip icon={<Error />} label={`Failed: ${failedCount}`} color="error" variant={failedCount > 0 ? 'filled' : 'outlined'} />
              {cancelledCount > 0 && (
                <Chip icon={<Cancel />} label={`Cancelled: ${cancelledCount}`} color="default" variant="filled" />
              )}
            </Box>
          </Paper>

          {(isPaused || isScheduled) && pausedReason && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {pausedReason}
              {isWaDisconnectPauseReason(pausedReason) && (
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  Reconnect your Personal WhatsApp session — sending will resume automatically.
                </Typography>
              )}
              {nextSendAt && (
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  Next activity: {formatNextSend(nextSendAt)}
                </Typography>
              )}
            </Alert>
          )}

          {isComplete && (
            <Alert severity={failedCount > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
              <Typography variant="body2">
                {sessionStatus === 'cancelled'
                  ? `Sending cancelled. ${successCount} sent, ${failedCount} failed, ${cancelledCount} cancelled.`
                  : `Sending complete! ${successCount} sent successfully${failedCount > 0 ? `, ${failedCount} failed` : ''}.`}
              </Typography>
            </Alert>
          )}

          {!isComplete && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Large campaigns run over multiple days with human-like pacing. You can close this dialog; sending continues in the background.
            </Alert>
          )}

          {recentUpdates.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Recent Activity
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell width="60px">Status</TableCell>
                      <TableCell>Recipient</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentUpdates.map((update, index) => (
                      <TableRow key={`${update.recordId}-${index}`}>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(update.status)}
                            label={update.status}
                            color={getStatusColor(update.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>{update.recipientNumber}</Typography>
                        </TableCell>
                        <TableCell>
                          {update.status === 'failed' && update.errorMessage && (
                            <Tooltip title={update.errorMessage}>
                              <Typography variant="caption" color="error" noWrap sx={{ maxWidth: 300, display: 'block' }}>
                                {update.errorMessage}
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {isComplete ? (
          <Button onClick={onClose} variant="contained">Close</Button>
        ) : (
          <>
            <Button onClick={onClose}>
              Minimize
            </Button>
            {(isPaused || isScheduled) && (
              <Button
                onClick={handleResume}
                variant="contained"
                startIcon={<PlayArrow />}
                disabled={isResuming}
              >
                {isResuming ? 'Resuming...' : 'Resume'}
              </Button>
            )}
            {!isPaused && (
              <Button onClick={handlePause} disabled={isPausing}>
                {isPausing ? 'Pausing...' : 'Pause'}
              </Button>
            )}
            <Button onClick={handleCancel} color="error" disabled={isCancelling}>
              {isCancelling ? 'Cancelling...' : 'Cancel Sending'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PersonalTemplateSendProgress;
